import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IContextualMenuItem } from "@fluentui/react";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import {
    useLazyCheckoutItemQuery,
    useLazyPublishItemQuery,
    useLazyUndoCheckoutQuery,
    useLazyUnpublishItemQuery,
} from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import authModule from "../../utilities/AuthModule";
import { CacheHelper } from "../../utilities/CacheHelper";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { DocubirdCache, OneNoteProgId, SharePointFolderContentTypeIds, officeFileType, validPdfExtensions, validSigningRequestExtensions } from "../../src/taskpane/config/constants";
import { brandedCommandbarIconStyles, menuItemIconStyles } from "../../src/taskpane/config/controlStyles";
import { SearchOpenExtensionHelper } from "../../src/taskpane/components/helpers/SearchOpenExtensionHelper";
import { ISearchOpenFileInfo } from "../../src/taskpane/interfaces/ISearchOpenFileInfo";
import { OneDriveUrlHelper } from "../../src/taskpane/components/helpers/oneDriveUrlHelper";
import { resolveShortcutOpenUrl } from "../../src/taskpane/components/helpers/shortcutOpen";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { ModeEnum } from "../../src/taskpane/components/enums/modeEnum";
import { ContentManagerCopyMoveModeEnum } from "../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { cancelContentManagerMoveTo, setContentManagerMoveTo } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { getValidHooks } from "../../src/taskpane/services/HooksService";
import { HookType } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { getFileItemIdFromRelativePath, getFilePdfByteContent, getItemCustomSelect, getItemWebViewUrl, getListItemWebUrl } from "../../src/taskpane/services/GraphService";
import { FileDownLoader } from "../../src/taskpane/components/helpers/fileDownloader";
import { apiFileConvertAndSaveAsPDF } from "../../src/taskpane/services/ApiService";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { IDocUrlDropdown } from "../../src/taskpane/interfaces/IDocUrlDropdown";
import { ILibraryPanelContext } from "../../src/taskpane/interfaces/ILibraryPanelContext";
import { CheckinCheckoutHelper } from "../../utilities/CheckinCheckoutHelper";
import shareToTeams from "../Services/ShareToTeamsService";
import { CMErrorModal } from "../Panels/CMErrorModal";
import { CMConfirmModal } from "../Panels/CMConfirmModal";
import { resolveAggregateSourceLabel } from "./helpers/aggregateSourceLabel";
import { buildHookValidationDirectory } from "./helpers/hookValidationDirectory";
import { ILocation, ISharepointFolderLocation, ISharepointListLocation } from "../Services/LocationService";
import { IAggregatedLibraryDescriptor, fetchAggregatedListSettings } from "../Services/AggregatedDocumentsService";
import { CMFilePreviewPanel, SearchSource } from "../Panels/CMFilePreviewPanel";
import { usePreviewTargetName } from "../Panels/usePreviewTargetName";
import { CMFileInfoPanel, InfoPanelMode } from "../Panels/CMFileInfoPanel";
import { CMVersionHistoryModal } from "../Panels/CMVersionHistoryModal";
import { CMCheckinModal } from "../Panels/CMCheckinModal";
import { CMVersionAppRejModal } from "../Panels/CMVersionAppRejModal";
import { CMRenameModal } from "../Panels/CMRenameModal";
import { CMFileSharePanel, SharePanelMode } from "../Panels/CMFileSharePanel";
import { WebPanel } from "../../src/taskpane/components/_v2/_shared/WebPanel/WebPanel";
import { previewWebPanelStyles } from "../../src/taskpane/config/controlStyles";
import { AutomationPanel } from "../../src/taskpane/components/_v2/components/Panels/AutomationPanel/AutomationPanel";
import { SigningPanel } from "../../src/taskpane/components/_v2/components/Panels/SigningPanel/SigningPanel";
import { HixUploadPanel } from "../../src/taskpane/components/panels/HixUploadPanel";
import { IHixUploadItem } from "../../src/taskpane/interfaces/IHix";
import { isCmUploadableFile } from "./MenuItems/hixUploadResolver";
// Icon components mirror the single-library grid's useMenuItem_* hooks 1:1 —
// custom SVGs inherit the neutral button foreground; bare iconProps iconNames
// would render in the theme accent color and break bar consistency.
import { Open20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Open20Regular";
import { DocumentGlobe20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentGlobe20Regular";
import { DocumentArrowRight20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentArrowRight20Regular";
import { PreviewLink20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/PreviewLink20Regular";
import { MoreVertical20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/MoreVertical20Regular";
import { Info20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Info20Regular";
import { RenameA20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/RenameA20Regular";
import { DocumentLandscapeSplitHint20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentLandscapeSplitHint20Regular";
import { Share20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Share20Regular";
import { Link20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Link20Regular";
import { ArrowCircleDownRight20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowCircleDownRight20Regular";
import { ArrowCircleUpLeft20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowCircleUpLeft20Regular";
import { ArrowUndo20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowUndo20Regular";
import { ArrowExportUp20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/ArrowExportUp20Regular";
import { Stack20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Stack20Regular";
import { StackOff20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/StackOff20Regular";
import { DocumentPdf20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentPdf20Regular";
import { DocumentPdf20Filled } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentPdf20Filled";
import { FlowDot20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/FlowDot20Regular";
import { DocumentSignature20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentSignature20Regular";
import { FolderArrowRight20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/FolderArrowRight20Regular";
import { DocumentCopy20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/DocumentCopy20Regular";
import {
    AGGREGATE_SOURCE_LIBRARY_NAME,
    AGGREGATE_SOURCE_LIST_ID,
    AGGREGATE_SOURCE_SITE_NAME,
    AGGREGATE_SOURCE_WEB_URL,
} from "./helpers/aggregateColumnRenderers";
import { resolveHixSiteTitle } from "../../src/taskpane/components/helpers/hixClientMatch";

/**
 * Row-scoped command composer for the cross-library "All documents" view: builds the
 * permission-gated menu slots AND owns the panels/dialogs they open, all wired to each
 * row's SOURCE library context instead of the Redux current directory.
 *
 * Gating rules mirror the single-library `useMenuItem_*` hooks, evaluated per selected
 * row and fail-closed:
 *  - commands act only when EVERY selected row permits them;
 *  - dialog-based commands that target one list (check-in, publish metadata,
 *    approve/reject) additionally require the selection to come from ONE library.
 */

export interface IUseAggregatedCommandsArgs {
    selectedItems: any[];
    libraries: ISharepointListLocation[];
    siteWebUrl: string;
    siteId: string;
    siteDisplayName?: string;
    openItemInBrowser: (item: any) => void;
    refreshItems: (items: any[]) => Promise<void>;
    clearSelection: () => void;
}

export interface IAggregatedCommandSlots {
    open: IContextualMenuItem | null;
    preview: IContextualMenuItem | null;
    // Icon-only far-item variants (library-view parity: preview/properties live in
    // the context menu as text items but on the bar only as far-item icons).
    previewIconOnly: IContextualMenuItem | null;
    properties: IContextualMenuItem | null;
    propertiesIconOnly: IContextualMenuItem | null;
    rename: IContextualMenuItem | null;
    replace: IContextualMenuItem | null;
    share: IContextualMenuItem | null;
    copyLink: IContextualMenuItem | null;
    shareToTeams: IContextualMenuItem | null;
    checkout: IContextualMenuItem | null;
    checkin: IContextualMenuItem | null;
    discardCheckout: IContextualMenuItem | null;
    publish: IContextualMenuItem | null;
    unpublish: IContextualMenuItem | null;
    versionAppRej: IContextualMenuItem | null;
    // The context menu's "More" submenu grouping the checkout/versioning state
    // machine (library parity: useMenuItem_More) — the BAR keeps them flat.
    more: IContextualMenuItem | null;
    versionHistory: IContextualMenuItem | null;
    downloadAsPDF: IContextualMenuItem | null;
    saveAsPDF: IContextualMenuItem | null;
    hixUpload: IContextualMenuItem | null;
    automation: IContextualMenuItem | null;
    signing: IContextualMenuItem | null;
    moveTo: IContextualMenuItem | null;
    copyTo: IContextualMenuItem | null;
}

const stripBraces = (uniqueId: string): string => uniqueId?.replace("{", "")?.replace("}", "");

const isFolderRow = (row: any): boolean =>
    row?.FSObjType === "1" || row?.ContentTypeId?.startsWith?.(SharePointFolderContentTypeIds.folder);

const isOneNoteRow = (row: any): boolean => row?.ProgId === OneNoteProgId;

export const useAggregatedCommands = (args: IUseAggregatedCommandsArgs): { slots: IAggregatedCommandSlots; panels: JSX.Element; openPropertiesPanel: () => void } => {
    const { selectedItems, libraries, siteWebUrl, siteId, siteDisplayName, openItemInBrowser, refreshItems, clearSelection } = args;

    const languageStrings = React.useContext(LanguageContext);
    const dispatch = useDispatch();

    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const mode = useSelector((state: RootState) => state.mode.mode);
    const userAccount = useSelector((state: RootState) => state.user?.account);
    const tenantId = useSelector((state: RootState) => state.user?.account?.tenantId);
    const tenantSharingEnabled = useSelector((state: RootState) => state.tenantInfo?.enableSharing);
    const tenantDisableFileRename = useSelector((state: RootState) => state.tenantInfo?.disableFileRename);
    const tenantCopyMoveToEnabled = useSelector((state: RootState) => state.tenantInfo?.enableCopyMoveTo);
    const tenantEnableApprovalHooks = useSelector((state: RootState) => state.user?.tenantEnableApprovalHooks);
    const tenantEnableHix = useSelector((state: RootState) => state.tenantInfo?.enableHix);
    // Same gate the library view's Open submenu uses (useMenuItem_Open).
    const allowOpenInSharePoint = useSelector((state: RootState) =>
        (state.user?.tenantAllowOpenInSharePoint ?? true)
        && !state.workingDirectories?.workingDirectories?.some(dir => dir?.type === ComponentTypes.Docubird));
    // Library parity: the properties panel embeds the preview unless the tenant opts out.
    const hideAutoPreview = useSelector((state: RootState) => state.tenantInfo?.hideAutoPreview);

    const [checkoutItem] = useLazyCheckoutItemQuery();
    const [undoCheckout] = useLazyUndoCheckoutQuery();
    const [publishItem] = useLazyPublishItemQuery();
    const [unpublishItem] = useLazyUnpublishItemQuery();

    const [isCommandBusy, setIsCommandBusy] = React.useState<boolean>(false);
    const [isPreviewOpen, setIsPreviewOpen] = React.useState<boolean>(false);
    const [infoPanel, setInfoPanel] = React.useState<{ open: boolean; panelMode: InfoPanelMode }>({ open: false, panelMode: InfoPanelMode.Update });
    const [isVersionHistoryOpen, setIsVersionHistoryOpen] = React.useState<boolean>(false);
    const [isRenameOpen, setIsRenameOpen] = React.useState<boolean>(false);
    const [sharePanel, setSharePanel] = React.useState<{ open: boolean; panelMode: SharePanelMode }>({ open: false, panelMode: SharePanelMode.Share });
    const [checkinDialog, setCheckinDialog] = React.useState<{ open: boolean; isPublish: boolean; context: ILibraryPanelContext | null }>({ open: false, isPublish: false, context: null });
    const [isAppRejOpen, setIsAppRejOpen] = React.useState<boolean>(false);
    const [isAutomationOpen, setIsAutomationOpen] = React.useState<boolean>(false);
    const [isSigningOpen, setIsSigningOpen] = React.useState<boolean>(false);
    const [isHixOpen, setIsHixOpen] = React.useState<boolean>(false);
    // Selection is captured when a multi-item dialog opens so a selection change
    // mid-dialog can't retarget the operation.
    const [dialogItems, setDialogItems] = React.useState<any[]>([]);
    const [appRejContext, setAppRejContext] = React.useState<ILibraryPanelContext | null>(null);
    const [versionHistoryContext, setVersionHistoryContext] = React.useState<ILibraryPanelContext | null>(null);
    // Per-item command failures are surfaced, never swallowed (parity with the
    // single-library hooks' CMErrorModal usage).
    const [commandError, setCommandError] = React.useState<string>("");
    // Synchronous re-entry guard: isCommandBusy disables menu items only after a
    // re-render, so a fast double-click could double-fire without this ref.
    const commandBusyRef = React.useRef<boolean>(false);

    // Single-item panels render on `flag && single`; if the row disappears while a
    // panel is open (per-row refresh removed it, selection cleared), the flag must
    // reset too or the panel would ghost-reopen on the NEXT single selection.
    const hasSingle = selectedItems.length === 1;
    React.useEffect(() => {
        if (!hasSingle) {
            setIsPreviewOpen(false);
            setIsVersionHistoryOpen(false);
            setIsRenameOpen(false);
            setInfoPanel(current => (current.open ? { open: false, panelMode: InfoPanelMode.Update } : current));
        }
    }, [hasSingle]);

    const single = selectedItems.length === 1 ? selectedItems[0] : null;
    // Name of the file a .url link points at, once the preview panel has resolved it.
    const [previewTargetName, setPreviewTargetName] = usePreviewTargetName(single);
    const sourceListIds = React.useMemo(
        () => Array.from(new Set(selectedItems.map(item => item?.[AGGREGATE_SOURCE_LIST_ID]).filter(Boolean))),
        [selectedItems]
    );
    const isSingleSourceLibrary = sourceListIds.length === 1;
    const singleListId: string | undefined = single?.[AGGREGATE_SOURCE_LIST_ID];

    // The selected row's library settings, prefetched for the Open submenu's
    // "open in app" gate (disableOpenInApp = ExcludeFromOfflineClient, exactly how
    // the browsing views derive it). Session-cached in the service, so this is a
    // one-time fetch per library. While unknown, non-Office "open in app" stays
    // hidden (fail closed); Office files bypass the setting like the library view.
    const [singleListSettings, setSingleListSettings] = React.useState<any | null>(null);

    const getLibraryLocation = React.useCallback((listId: string): ISharepointListLocation | undefined =>
        (libraries ?? []).find(library => library?.sharePointListId === listId), [libraries]);

    // The Graph DRIVE id of a location. For library locations the location id IS the
    // drive id; for folder-shaped locations (Tess folder dossiers ride the same array)
    // `id` is the FOLDER's drive-ITEM id and the drive id lives in `driveId` — reading
    // `id` there would hand Graph consumers (version panels, rename targeting) a value
    // that is not a drive at all.
    const getLocationDriveId = (location: ISharepointListLocation): string =>
        (location as ISharepointFolderLocation).driveId ?? location.id;

    const getDescriptor = React.useCallback((listId: string): IAggregatedLibraryDescriptor | null => {
        const location = getLibraryLocation(listId);
        return location
            ? { listId, libraryName: location.displayName, webUrl: location.siteUrl ?? siteWebUrl, driveId: getLocationDriveId(location) }
            : null;
    }, [getLibraryLocation, siteWebUrl]);

    React.useEffect(() => {
        let cancelled = false;
        setSingleListSettings(null);
        if (!singleListId) {
            return undefined;
        }
        (async () => {
            try {
                const descriptor = getDescriptor(singleListId);
                if (!descriptor) {
                    return;
                }
                const settings = await fetchAggregatedListSettings(await authModule.getSharePointAccessToken(), descriptor);
                if (!cancelled) {
                    setSingleListSettings(settings);
                }
            } catch (error) {
                logger(error, "useAggregatedCommands.tsx (singleListSettings)");
            }
        })();
        return () => { cancelled = true; };
    }, [singleListId, getDescriptor]);

    const getDocUrlDropdown = React.useCallback((listId: string): IDocUrlDropdown | undefined => {
        const location = getLibraryLocation(listId);
        return location
            ? {
                key: listId,
                text: location.displayName,
                webUrl: location.siteUrl ?? siteWebUrl,
                documentLibrary: location.displayName,
                driveId: getLocationDriveId(location),
                sharePointListId: listId,
            } as IDocUrlDropdown
            : undefined;
    }, [getLibraryLocation, siteWebUrl]);

    const buildLibraryContext = React.useCallback(async (listId: string): Promise<ILibraryPanelContext | null> => {
        const descriptor = getDescriptor(listId);
        if (!descriptor) {
            return null;
        }
        const settings = await fetchAggregatedListSettings(await authModule.getSharePointAccessToken(), descriptor);
        return {
            webUrl: descriptor.webUrl,
            sharePointListId: descriptor.listId,
            driveId: descriptor.driveId,
            libraryName: descriptor.libraryName,
            enableMinorVersions: settings?.EnableMinorVersions === true,
        };
    }, [getDescriptor]);

    // Directory-shaped stand-in for pure per-library checks (hook validity); a client-group
    // library is checked against its own site.
    const pseudoDirectoryFor = React.useCallback((listId: string): IPageSettings =>
        buildHookValidationDirectory(getLibraryLocation(listId), siteDisplayName), [siteDisplayName, getLibraryLocation]);

    const every = (predicate: (row: any) => boolean): boolean =>
        selectedItems.length > 0 && selectedItems.every(predicate);

    // Same identity semantics as the single-library grid (trimmed, case-insensitive,
    // blank identity counts as own checkout) — a divergent comparison here would hide
    // Check In in the aggregate view for rows the library view offers it on.
    const compareCheckoutUser = (row: any): boolean =>
        Array.isArray(row?.CheckoutUser) && row.CheckoutUser.length > 0
        && row.CheckoutUser.every((entry: any) => CheckinCheckoutHelper.compareCurrentUser(entry, userAccount?.username));

    const runPerItem = React.useCallback(async (action: (row: any, accessToken: string) => Promise<any>, items: any[]): Promise<void> => {
        if (commandBusyRef.current) {
            return;
        }
        commandBusyRef.current = true;
        setIsCommandBusy(true);
        let failedCount = 0;
        try {
            const accessToken = await authModule.getSharePointAccessToken();
            await Promise.all(items.map(async row => {
                try {
                    await action(row, accessToken);
                } catch (error) {
                    failedCount++;
                    logger(error, "useAggregatedCommands.tsx (runPerItem)");
                }
            }));
            await refreshItems(items);
        } catch (error) {
            failedCount = items.length;
            logger(error, "useAggregatedCommands.tsx (runPerItem)");
        }
        if (failedCount > 0) {
            setCommandError(`${languageStrings.ActionFailed} (${failedCount}/${items.length})`);
        }
        setIsCommandBusy(false);
        commandBusyRef.current = false;
    }, [refreshItems, languageStrings]);

    const hasValidHooksForSelection = React.useCallback((hookFilter: (hook: any) => boolean): boolean => {
        const allHooks = CacheHelper.get(DocubirdCache.hooks);
        if (!Array.isArray(allHooks)) {
            return false;
        }
        const relevant = allHooks.filter(hookFilter);
        if (relevant.length === 0) {
            return false;
        }
        return sourceListIds.every(listId => getValidHooks(relevant, pseudoDirectoryFor(listId)).length > 0);
    }, [sourceListIds, pseudoDirectoryFor]);

    // Arming a copy/move is a two-step in the library view: a dialog names the operation
    // and tells the user to go pick a destination, and only its OK arms anything. This
    // flow used to arm on the click itself, so selecting files in the aggregate grid and
    // pressing Move silently swapped the view out from under the user with no
    // acknowledgement that anything had been registered. Same two steps here.
    //
    // The pending selection is captured rather than re-read on confirm, matching how
    // every other multi-item dialog in this hook works (see dialogItems): the grid is
    // still live behind the modal, and the operation must target what was selected when
    // it opened.
    const [pendingCopyMove, setPendingCopyMove] = React.useState<{ mode: ContentManagerCopyMoveModeEnum; items: any[] } | null>(null);

    const startMoveOrCopy = React.useCallback((copyMode: ContentManagerCopyMoveModeEnum) => {
        // createCopyJobs rejects any batch containing duplicate FileLeafRef values —
        // normal in a cross-library selection. Fail here, before the user navigates
        // to a destination, instead of aborting the whole move at the last step.
        const names = selectedItems.map(row => row?.FileLeafRef?.toLowerCase()).filter(Boolean);
        if (new Set(names).size !== names.length) {
            setCommandError(languageStrings.MultipleFilesFoldersCopyMove);
            return;
        }
        setPendingCopyMove({ mode: copyMode, items: [...selectedItems] });
    }, [selectedItems, languageStrings]);

    const confirmMoveOrCopy = React.useCallback(() => {
        if (!pendingCopyMove) {
            return;
        }
        const { mode: copyMode, items } = pendingCopyMove;
        setPendingCopyMove(null);

        // Bridge into the existing move machinery: stash the rows (they carry FileRef/
        // FileLeafRef/UniqueId — createCopyJobs derives source URLs per row), enable
        // move mode, and hand the user to the locations view to navigate to the
        // destination, where the existing confirm/createCopyJobs flow completes it.
        // Cancel rather than reset: an email copy may be the thing being displaced, and
        // its payload lives outside redux (the exchange item store and the cached
        // property mappings). Only the cancel action clears those.
        dispatch(cancelContentManagerMoveTo());
        dispatch(setContentManagerMoveTo({
            mode: copyMode,
            moveToEnabled: true,
            workingDirectories,
            selectedFiles: items,
            // Anchored on a ROW's own web url, falling back to the ambient site:
            // createCopyJobs only takes the ORIGIN off this (buildCopyJobSourcePaths —
            // per-row FileRefs supply the paths), and rows always carry a real web url,
            // while a Tess directory's ambient webUrl (the inherited ancestor's) can be
            // absent — which would fail the whole batch as unresolvable.
            sourceSiteUrl: items[0]?.[AGGREGATE_SOURCE_WEB_URL] ?? siteWebUrl,
            // Display text ONLY. It must never go in sourceServerRelPath: that field
            // feeds path building, and a label there once made the copy fail outright.
            //
            // Derived from the rows' own source libraries, NOT from the name of this
            // view: "All Documents" is a surface spanning every library on the site, so
            // no selected file lives there and it names nothing the user could open.
            sourceDisplayName: resolveAggregateSourceLabel(
                items,
                listId => getLibraryLocation(listId)?.displayName,
                siteDisplayName
            ),
            // Deliberately NOT clearing sourceServerRelPath here: this reducer merges
            // strings by truthiness, so passing "" would be a silent no-op. The
            // cancelContentManagerMoveTo() above is what actually leaves it empty.
            //
            // The library flow gets its count from the destination-side confirm dialog,
            // which reads selectedFiles; this flow needs it up front or the command bar
            // shows no "N items to move".
            progressCountTotal: items.length,
        }));
        clearSelection();
        // No explicit view switch: ContentBodyLibraries shows the locations list for as
        // long as a copy/move is waiting for a destination, and restores the user's own
        // view when it ends. Switching imperatively here is what used to overwrite their
        // All Documents preference for the site.
    }, [pendingCopyMove, dispatch, workingDirectories, siteWebUrl, siteDisplayName, getLibraryLocation, clearSelection, languageStrings]);

    const downloadSelectedAsPdf = React.useCallback(async (row: any): Promise<void> => {
        setIsCommandBusy(true);
        try {
            const pdfContent = await getFilePdfByteContent({ id: row.itemId, driveId: row.driveId });
            const baseName: string = row.FileLeafRef?.replace(/\.[^.]+$/, "") ?? "document";
            FileDownLoader.downloadFile(pdfContent, `${baseName}.pdf`);
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (downloadSelectedAsPdf)");
        }
        setIsCommandBusy(false);
    }, []);

    const saveSelectedAsPdf = React.useCallback(async (row: any): Promise<void> => {
        setIsCommandBusy(true);
        try {
            // Aggregate-view simplification: always keep both files (EnsureUniqueFilename)
            // instead of the single-library duplicate/version selector dialog.
            await apiFileConvertAndSaveAsPDF({
                graphToken: await authModule.getGraphAccessToken(),
                sharePointToken: await authModule.getSharePointAccessToken(),
                tenantGuid: tenantId,
                accountId: userAccount?.accountId,
                driveId: row.driveId,
                itemId: row.itemId,
                siteUrl: row[AGGREGATE_SOURCE_WEB_URL],
                listId: row[AGGREGATE_SOURCE_LIST_ID],
                teamId: undefined,
                channelId: undefined,
                fileUniqueId: stripBraces(row.UniqueId),
                EnsureUniqueFilename: true,
                checkinType: null,
            } as any);
            await refreshItems([row]);
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (saveSelectedAsPdf)");
        }
        setIsCommandBusy(false);
    }, [tenantId, userAccount, refreshItems]);

    const shareSelectedToTeams = React.useCallback(async (row: any): Promise<void> => {
        setIsCommandBusy(true);
        try {
            await shareToTeams(
                row.driveId,
                row.itemId,
                !isFolderRow(row),
                row.FileLeafRef,
                row.ContentTypeId,
                row.weburl
            );
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (shareSelectedToTeams)");
        }
        setIsCommandBusy(false);
    }, []);

    // ---- Open submenu (library-view parity: useMenuItem_Open's browser/app paths,
    // ---- resolved from the ROW's driveId/itemId instead of the ambient directory).

    const resolveRowItemId = React.useCallback(async (row: any): Promise<string | undefined> => {
        let itemId: string | undefined = row?.itemId;
        if (!itemId && row?.driveId) {
            try {
                const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), row.driveId, null));
                const folderUrl = new URL(driveUrl.origin + row.FileRef);
                itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), row.driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
            } catch (error) {
                logger(error, "useAggregatedCommands.tsx (resolveRowItemId)");
            }
        }
        return itemId;
    }, []);

    const openRowInBrowser = React.useCallback(async (row: any): Promise<void> => {
        // URL shortcut files open their stored target directly.
        if (row?.File_x0020_Type === "url") {
            const url = await resolveShortcutOpenUrl(row);
            if (url) {
                window.open(url, "_blank");
                return;
            }
        }
        setIsCommandBusy(true);
        try {
            const itemId = await resolveRowItemId(row);
            // sharepointIds selected alongside webUrl because Graph sometimes returns a
            // more suitable Office web-app url format that way (library-view parity).
            const fileItem = await getItemCustomSelect(await authModule.getGraphAccessToken(), row.driveId, itemId, "webUrl,sharepointIds");
            const url = fileItem?.webUrl || await getItemWebViewUrl(await authModule.getGraphAccessToken(), row.driveId, itemId);
            window.open(url, "_blank");
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (openRowInBrowser)");
        }
        setIsCommandBusy(false);
    }, [resolveRowItemId]);

    const openRowInApp = React.useCallback(async (row: any, fileInfo: ISearchOpenFileInfo): Promise<void> => {
        setIsCommandBusy(true);
        try {
            let openSelf: boolean = true;
            let url: string;
            if (!fileInfo.isOfficeFile) {
                // odopen:// deep link into the OneDrive client — needs the SP ids.
                const fileId: string = stripBraces(row.UniqueId);
                const fileName: string = row.FileLeafRef;
                const userEmail: string = userAccount?.username;
                const userId: string = userAccount?.uniqueId;

                const siteIdParts: string[] = siteId?.split(",");
                let spSiteId: string = Array.isArray(siteIdParts) && siteIdParts.length > 1 && siteIdParts[1] ? siteIdParts[1] : undefined;
                let listId: string = row[AGGREGATE_SOURCE_LIST_ID];
                let webUrl: string = row[AGGREGATE_SOURCE_WEB_URL];

                // The ambient siteId names the content manager's CURRENT site — but a
                // row can live on another site entirely (Tess dossiers resolve
                // cross-site), where that site GUID would deep-link the wrong site.
                // A row whose own web differs from the ambient one must resolve its
                // ids from the file itself.
                const normalizeWeb = (url?: string): string => String(url ?? "").trim().replace(/\/+$/, "").toLowerCase();
                const isForeignWeb: boolean = !!webUrl && !!siteWebUrl && normalizeWeb(webUrl) !== normalizeWeb(siteWebUrl);

                if (!spSiteId || !listId || !webUrl || isForeignWeb) {
                    const itemId = await resolveRowItemId(row);
                    const file = row.driveId && itemId && await getItemCustomSelect(await authModule.getGraphAccessToken(), row.driveId, itemId, "sharepointIds");
                    if (file?.sharepointIds) {
                        spSiteId = file.sharepointIds.siteId;
                        listId = file.sharepointIds.listId;
                        webUrl = file.sharepointIds.siteUrl;
                    }
                }

                url = OneDriveUrlHelper.getOpenInOneDriveDesktopUrl(fileId, spSiteId, listId, userEmail, userId, webUrl, fileName);
            } else {
                const itemId = await resolveRowItemId(row);
                const fileWebUrl: string = await getListItemWebUrl(await authModule.getGraphAccessToken(), row.driveId, itemId);
                url = `${fileInfo.localUrlPrepended}${fileWebUrl}`;

                if (!fileInfo.localUrlPrepended) {
                    openSelf = false;
                }
            }

            window.open(url, openSelf ? "_self" : "_blank");
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (openRowInApp)");
        }
        setIsCommandBusy(false);
    }, [resolveRowItemId, userAccount, siteId, siteWebUrl]);

    const openCheckinDialog = React.useCallback(async (isPublish: boolean): Promise<void> => {
        if (commandBusyRef.current) {
            return;
        }
        commandBusyRef.current = true;
        setIsCommandBusy(true);
        try {
            const context = await buildLibraryContext(sourceListIds[0]);
            if (context) {
                setDialogItems(selectedItems);
                setCheckinDialog({ open: true, isPublish, context });
            }
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (openCheckinDialog)");
        }
        setIsCommandBusy(false);
        commandBusyRef.current = false;
    }, [buildLibraryContext, sourceListIds, selectedItems]);

    // Approve/reject and version history need async per-library context; both follow
    // the same safe open pattern as check-in: busy-guard the whole fetch and set the
    // shared dialogItems ONLY together with the open flag, so a concurrent command
    // cannot retarget the dialog to a different selection mid-flight.
    const openAppRejDialog = React.useCallback(async (): Promise<void> => {
        if (commandBusyRef.current) {
            return;
        }
        commandBusyRef.current = true;
        setIsCommandBusy(true);
        try {
            const context = await buildLibraryContext(sourceListIds[0]);
            if (context) {
                setDialogItems(selectedItems);
                setAppRejContext(context);
                setIsAppRejOpen(true);
            }
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (openAppRejDialog)");
        }
        setIsCommandBusy(false);
        commandBusyRef.current = false;
    }, [buildLibraryContext, sourceListIds, selectedItems]);

    const openVersionHistory = React.useCallback(async (row: any): Promise<void> => {
        if (commandBusyRef.current) {
            return;
        }
        commandBusyRef.current = true;
        setIsCommandBusy(true);
        try {
            // Context includes EnableMinorVersions so the modal's minor-version
            // affordances reflect the row's OWN library, not the last-browsed one.
            const context = await buildLibraryContext(row?.[AGGREGATE_SOURCE_LIST_ID]);
            setVersionHistoryContext(context);
            setIsVersionHistoryOpen(true);
        } catch (error) {
            logger(error, "useAggregatedCommands.tsx (openVersionHistory)");
        }
        setIsCommandBusy(false);
        commandBusyRef.current = false;
    }, [buildLibraryContext]);

    const slots = React.useMemo<IAggregatedCommandSlots>(() => {
        const noFoldersOrNotes = every(row => !isFolderRow(row) && !isOneNoteRow(row));
        const everyoneEditable = every(row => row?.EffectiveBasePermissions?.canEditListItem === true);
        const allAvailable = every(row => !row?.CheckoutUser);
        const allCheckedOut = every(row => Array.isArray(row?.CheckoutUser) && row.CheckoutUser.length > 0);
        const allCheckedOutByMe = allCheckedOut && every(compareCheckoutUser);
        // === true (not !== false): rows whose PermMask failed to decode have null
        // permissions and must NOT enable overriding someone else's checkout.
        const canOverrideCheckout = allCheckedOut && every(row => row?.EffectiveBasePermissions?.canCancelCheckout === true);

        const makeItem = (key: string, text: string, onClick: () => void, icon?: () => JSX.Element): IContextualMenuItem => ({
            key,
            text,
            disabled: isCommandBusy,
            iconProps: {},
            onRenderIcon: icon ? () => icon() : undefined,
            onClick: () => { onClick(); },
        });

        // Open submenu — same structure/gating as the library view's useMenuItem_Open:
        // "Open in browser" + "Open in app", branded with the file-type icon; app is
        // gated on canOpenItems, the library's ExcludeFromOfflineClient setting (Office
        // files bypass it), and never offered for url shortcuts; browser-only types
        // respect the tenant's allowOpenInSharePoint switch.
        const singleFileInfo: ISearchOpenFileInfo | null = single
            ? SearchOpenExtensionHelper.determineOfficeFileType(single.ProgId === OneNoteProgId ? OneNoteProgId : single[".fileType"])
            : null;
        const disableOpenInApp: boolean = singleListSettings ? singleListSettings.ExcludeFromOfflineClient === true : true;

        let openSubItems: IContextualMenuItem[] = [];
        if (single && !isFolderRow(single)
            && (single?.EffectiveBasePermissions?.canEditListItem === true || single?.EffectiveBasePermissions?.canOpenItems === true)) {
            const browser: IContextualMenuItem = {
                key: "aggOpenBrowser",
                text: `${languageStrings.OpenIn} ${languageStrings.Browser}`,
                iconProps: {},
                onRenderIcon: () => (singleFileInfo?.iconSrc
                    ? <img src={singleFileInfo.iconSrc} className={brandedCommandbarIconStyles} />
                    : <DocumentGlobe20Regular className={menuItemIconStyles} />),
                onClick: () => { openRowInBrowser(single); },
            };

            const app: IContextualMenuItem | null = singleFileInfo
                && single?.EffectiveBasePermissions?.canOpenItems === true
                && (!disableOpenInApp || singleFileInfo.isOfficeFile)
                && single?.File_x0020_Type !== "url"
                ? {
                    key: "aggOpenApp",
                    text: `${languageStrings.OpenIn} ${languageStrings.App}`,
                    iconProps: {},
                    onRenderIcon: () => (singleFileInfo?.iconSrc
                        ? <img src={singleFileInfo.iconSrc} className={brandedCommandbarIconStyles} />
                        : <DocumentArrowRight20Regular className={menuItemIconStyles} />),
                    onClick: () => { openRowInApp(single, singleFileInfo); },
                }
                : null;

            openSubItems = singleFileInfo?.type === officeFileType.Browser
                ? (allowOpenInSharePoint === false ? [] : [browser])
                : ([browser, app].filter(Boolean) as IContextualMenuItem[]);
        }

        const open: IContextualMenuItem | null = openSubItems.length > 0
            ? {
                key: "aggOpen",
                text: languageStrings.Open,
                disabled: isCommandBusy,
                iconProps: {},
                onRenderIcon: () => (singleFileInfo?.iconSrc
                    ? <img src={singleFileInfo.iconSrc} style={{ marginLeft: 4, marginRight: 4, width: 16, height: 16 }} />
                    : <Open20Regular className={menuItemIconStyles} />),
                subMenuProps: { items: openSubItems },
            }
            : null;

        const preview = single && !isFolderRow(single) && single.itemId
            ? makeItem("aggPreview", languageStrings.ShowPreview, () => setIsPreviewOpen(true), () => <PreviewLink20Regular className={menuItemIconStyles} />)
            : null;

        const previewIconOnly: IContextualMenuItem | null = preview
            ? {
                key: "aggPreviewIconOnly",
                iconProps: {},
                onRenderIcon: () => <PreviewLink20Regular className={menuItemIconStyles} />,
                iconOnly: true,
                disabled: isCommandBusy,
                tooltipHostProps: { content: <span>{languageStrings.ShowPreview}</span> },
                onClick: () => setIsPreviewOpen(true),
            }
            : null;

        const properties = single && single?.EffectiveBasePermissions?.canEditListItem !== false
            ? makeItem("aggProperties", languageStrings.Properties, () => setInfoPanel({ open: true, panelMode: InfoPanelMode.Update }), () => <Info20Regular className={menuItemIconStyles} />)
            : null;

        const propertiesIconOnly: IContextualMenuItem | null = properties
            ? {
                key: "aggPropertiesIconOnly",
                iconProps: {},
                onRenderIcon: () => <Info20Regular className={menuItemIconStyles} />,
                iconOnly: true,
                disabled: isCommandBusy,
                tooltipHostProps: { content: <span>{languageStrings.Properties}</span> },
                onClick: () => setInfoPanel({ open: true, panelMode: InfoPanelMode.Update }),
            }
            : null;

        const rename = single && !tenantDisableFileRename
            && single?.EffectiveBasePermissions?.canDeleteListItem === true && single.FSObjType === "0"
            ? makeItem("aggRename", languageStrings.RenameOnly, () => setIsRenameOpen(true), () => <RenameA20Regular className={menuItemIconStyles} />)
            : null;

        const replace = single && !isFolderRow(single) && !isOneNoteRow(single)
            && single?.EffectiveBasePermissions?.canDeleteListItem === true
            && (!single?.CheckoutUser || compareCheckoutUser(single))
            ? makeItem("aggReplace", languageStrings.Replace, () => setInfoPanel({ open: true, panelMode: InfoPanelMode.Replace }), () => <DocumentLandscapeSplitHint20Regular className={menuItemIconStyles} />)
            : null;

        const share = single && tenantSharingEnabled
            ? makeItem("aggShare", languageStrings.Share, () => { setDialogItems([single]); setSharePanel({ open: true, panelMode: SharePanelMode.Share }); }, () => <Share20Regular className={menuItemIconStyles} />)
            : null;

        const copyLink = selectedItems.length > 0 && tenantSharingEnabled
            ? makeItem("aggCopyLink", languageStrings.CopyLink, () => { setDialogItems(selectedItems); setSharePanel({ open: true, panelMode: SharePanelMode.Copy }); }, () => <Link20Regular className={menuItemIconStyles} />)
            : null;

        const shareToTeamsItem = single && mode === ModeEnum.contentManagerTeams && !isFolderRow(single) && single.itemId
            ? { ...makeItem("aggShareToTeams", `${languageStrings.Share} Teams`, () => { shareSelectedToTeams(single); }), iconProps: { iconName: "TeamsLogo" }, onRenderIcon: undefined }
            : null;

        const checkout = noFoldersOrNotes && allAvailable && everyoneEditable
            ? makeItem("aggCheckout", languageStrings.CheckOut, () => {
                runPerItem((row, accessToken) => checkoutItem({ siteUrl: row[AGGREGATE_SOURCE_WEB_URL], uniqueId: stripBraces(row.UniqueId), accessToken }).unwrap(), selectedItems);
            }, () => <ArrowCircleDownRight20Regular className={menuItemIconStyles} />)
            : null;

        const checkin = noFoldersOrNotes && everyoneEditable && isSingleSourceLibrary
            && (allCheckedOutByMe || canOverrideCheckout)
            ? makeItem("aggCheckin", languageStrings.CheckIn, () => { openCheckinDialog(false); }, () => <ArrowCircleUpLeft20Regular className={menuItemIconStyles} />)
            : null;

        const discardCheckout = noFoldersOrNotes && (allCheckedOutByMe || canOverrideCheckout)
            ? makeItem("aggDiscardCheckout", languageStrings.DiscardCheckOut, () => {
                runPerItem((row, accessToken) => undoCheckout({ siteUrl: row[AGGREGATE_SOURCE_WEB_URL], uniqueId: stripBraces(row.UniqueId), accessToken }).unwrap(), selectedItems);
            }, () => <ArrowUndo20Regular className={menuItemIconStyles} />)
            : null;

        const singleModeration: string = single?.["_ModerationStatus."];
        const singleHasMinorVersion = single ? (+single._UIVersion % 512) > 0 : false;
        const singleMajorVersion = single ? Math.floor(+single._UIVersion / 512) : 0;

        const publish = single && !isFolderRow(single) && !single.CheckoutUser
            && singleHasMinorVersion && singleModeration !== "2" && isSingleSourceLibrary
            ? makeItem("aggPublish", languageStrings.Publish, () => {
                if (singleModeration === "3" || singleModeration === "1") {
                    runPerItem((row, accessToken) => publishItem({ siteUrl: row[AGGREGATE_SOURCE_WEB_URL], uniqueId: stripBraces(row.UniqueId), accessToken, comment: "" }).unwrap(), [single]);
                } else {
                    openCheckinDialog(true);
                }
            }, () => <ArrowExportUp20Regular className={menuItemIconStyles} />)
            : null;

        const unpublish = single && !isFolderRow(single)
            && (( !single.CheckoutUser && singleMajorVersion > 0 && !singleHasMinorVersion) || singleModeration === "2")
            && single?.EffectiveBasePermissions?.canEditListItem === true
            ? makeItem("aggUnpublish", languageStrings.Unpublish, () => {
                runPerItem((row, accessToken) => unpublishItem({ siteUrl: row[AGGREGATE_SOURCE_WEB_URL], uniqueId: stripBraces(row.UniqueId), accessToken }).unwrap(), [single]);
            }, () => <StackOff20Regular className={menuItemIconStyles} />)
            : null;

        const versionAppRej = selectedItems.length > 0 && noFoldersOrNotes && isSingleSourceLibrary
            && every(row => row?.["_ModerationStatus."] === "2")
            && every(row => row?.EffectiveBasePermissions?.canApproveItems === true)
            ? makeItem("aggVersionAppRej", `${languageStrings.Approve}/${languageStrings.Reject}`, () => { openAppRejDialog(); }, () => <ArrowCircleDownRight20Regular className={menuItemIconStyles} />)
            : null;

        const versionHistory = single && !isFolderRow(single)
            ? makeItem("aggVersionHistory", languageStrings.VersionHistory, () => { openVersionHistory(single); }, () => <Stack20Regular className={menuItemIconStyles} />)
            : null;

        const isConvertible = single && validPdfExtensions.some(ext => single[".fileType"]?.replace(".", "")?.toLowerCase() === ext);

        const downloadAsPDF = single && !isFolderRow(single) && isConvertible && single.itemId
            && single?.EffectiveBasePermissions?.canOpenItems === true
            ? makeItem("aggDownloadAsPDF", `${languageStrings.Download} PDF`, () => { downloadSelectedAsPdf(single); }, () => <DocumentPdf20Regular className={menuItemIconStyles} />)
            : null;

        const saveAsPDF = single && !isFolderRow(single) && isConvertible && single.itemId
            && single?.EffectiveBasePermissions?.canOpenItems === true
            && single?.EffectiveBasePermissions?.canAddListItem === true
            ? makeItem("aggSaveAsPDF", `${languageStrings.Save} PDF`, () => { saveSelectedAsPdf(single); }, () => <DocumentPdf20Filled className={menuItemIconStyles} />)
            : null;

        // No per-library hook validation here (unlike automation/signing), so a
        // cross-library selection is fine: each file uploads by its own driveId/itemId.
        const hixUpload = tenantEnableHix && selectedItems.filter(isCmUploadableFile).length > 0
            ? makeItem("aggHixUpload", languageStrings.UploadToHix, () => { setDialogItems(selectedItems); setIsHixOpen(true); },
                () => <img src={`${process.env.baseUri}/assets/hix_logo.png`} alt="HIX" className={menuItemIconStyles} style={{ width: 20, height: "auto" }} />)
            : null;

        // Single-source only: the panels validate hook whitelists/blacklists against
        // ONE library location (hookValidationLocation) — a mixed-library selection
        // cannot be expressed and would bypass per-library hook restrictions.
        const automation = selectedItems.length > 0 && noFoldersOrNotes && tenantEnableApprovalHooks
            && isSingleSourceLibrary
            && hasValidHooksForSelection(hook => hook?.typeId === HookType.Approval || hook?.typeId === HookType.BatchApproval || hook?.typeId === HookType.Generic)
            ? makeItem("aggAutomation", languageStrings.Automation, () => { setDialogItems(selectedItems); setIsAutomationOpen(true); }, () => <FlowDot20Regular className={menuItemIconStyles} />)
            : null;

        const signing = selectedItems.length > 0 && noFoldersOrNotes
            && isSingleSourceLibrary
            && CacheHelper.get(DocubirdCache.signingEnabled) === true
            && every(row => validSigningRequestExtensions.includes(row?.File_x0020_Type?.toUpperCase()))
            && hasValidHooksForSelection(hook => hook?.typeId === HookType.Sign)
            ? makeItem("aggSigning", languageStrings.SigningRequest, () => { setDialogItems(selectedItems); setIsSigningOpen(true); }, () => <DocumentSignature20Regular className={menuItemIconStyles} />)
            : null;

        const moveTo = selectedItems.length > 0 && tenantCopyMoveToEnabled
            && every(row => row?.EffectiveBasePermissions?.canDeleteListItem === true)
            ? makeItem("aggMoveTo", languageStrings.MoveTo, () => startMoveOrCopy(ContentManagerCopyMoveModeEnum.Move), () => <FolderArrowRight20Regular className={menuItemIconStyles} />)
            : null;

        const copyTo = selectedItems.length > 0 && tenantCopyMoveToEnabled
            && every(row => row?.EffectiveBasePermissions?.canOpenItems === true)
            ? makeItem("aggCopyTo", languageStrings.CopyTo, () => startMoveOrCopy(ContentManagerCopyMoveModeEnum.Copy), () => <DocumentCopy20Regular className={menuItemIconStyles} />)
            : null;

        // Library-parity "More" (useMenuItem_More): same members, same order, same
        // busy gate; null when the whole state machine is gated off.
        const moreSubItems = [checkout, checkin, discardCheckout, publish, unpublish, versionAppRej]
            .filter(Boolean) as IContextualMenuItem[];
        const more: IContextualMenuItem | null = moreSubItems.length > 0
            ? {
                key: "aggMore",
                text: languageStrings.More,
                disabled: isCommandBusy,
                iconProps: {},
                onRenderIcon: () => <MoreVertical20Regular className={menuItemIconStyles} />,
                subMenuProps: { items: moreSubItems },
            }
            : null;

        return {
            open, preview, previewIconOnly, properties, propertiesIconOnly, rename, replace, share, copyLink,
            shareToTeams: shareToTeamsItem, checkout, checkin, discardCheckout, publish, unpublish,
            versionAppRej, more, versionHistory, downloadAsPDF, saveAsPDF, hixUpload, automation, signing, moveTo, copyTo,
        };
    }, [selectedItems, single, isCommandBusy, isSingleSourceLibrary, sourceListIds, languageStrings, mode,
        userAccount, tenantSharingEnabled, tenantDisableFileRename, tenantCopyMoveToEnabled, tenantEnableApprovalHooks, tenantEnableHix,
        allowOpenInSharePoint, singleListSettings, openRowInBrowser, openRowInApp,
        openItemInBrowser, runPerItem, openCheckinDialog, openAppRejDialog, openVersionHistory,
        startMoveOrCopy, downloadSelectedAsPdf, saveSelectedAsPdf, shareSelectedToTeams, hasValidHooksForSelection,
        checkoutItem, undoCheckout, publishItem, unpublishItem, buildLibraryContext]);

    const singleContext = single ? {
        listId: single[AGGREGATE_SOURCE_LIST_ID] as string,
        docUrlDropdown: getDocUrlDropdown(single[AGGREGATE_SOURCE_LIST_ID]),
        panelContext: {
            webUrl: single[AGGREGATE_SOURCE_WEB_URL],
            sharePointListId: single[AGGREGATE_SOURCE_LIST_ID],
            driveId: single.driveId,
            libraryName: single[AGGREGATE_SOURCE_LIBRARY_NAME],
        } as ILibraryPanelContext,
    } : null;

    const closeAndRefresh = (items: any[], close: () => void) => {
        close();
        refreshItems(items);
    };

    // Double-click entry point (library parity: onItemInvoked → showFileInfo).
    const openPropertiesPanel = React.useCallback((): void => {
        setInfoPanel({ open: true, panelMode: InfoPanelMode.Update });
    }, []);

    // Whether the selected row can render in the preview panel — mirrors the library's
    // getIsPreviewNotSupported (document content type, not OneNote) plus this view's
    // own preview prerequisite (Graph itemId parsed from .spItemUrl).
    const isSinglePreviewSupported: boolean = !!single
        && !isFolderRow(single)
        && !!single.itemId
        && single?.ContentTypeId?.startsWith?.(SharePointFolderContentTypeIds.document) === true
        && !isOneNoteRow(single);

    const panels = (
        <>
            {isPreviewOpen && single && (
                <WebPanel isOpen={isPreviewOpen} width="1100px" closePanel={() => setIsPreviewOpen(false)} headerText={previewTargetName ?? single?.FileLeafRef} styles={previewWebPanelStyles}>
                    <CMFilePreviewPanel
                        selectedItem={single}
                        searchSource={SearchSource.Api}
                        // Single-item preview: without index/count the panel's
                        // document-level arrow-key handler computes both nav buttons
                        // as enabled and calls the (absent) `move` prop — a TypeError
                        // on every keypress. 0-of-1 disables both directions.
                        selectedItemIndex={0}
                        totalItemCount={1}
                        onTargetResolved={setPreviewTargetName}
                    />
                </WebPanel>
            )}
            {infoPanel.open && single && (
                <CMFileInfoPanel
                    item={single}
                    panelIsOpen={infoPanel.open}
                    infoPanelMode={infoPanel.panelMode}
                    selectedDocumentLibrary={singleContext?.docUrlDropdown}
                    userPermissions={single.EffectiveBasePermissions}
                    closePanel={() => setInfoPanel({ open: false, panelMode: InfoPanelMode.Update })}
                    closeOnSuccess={() => closeAndRefresh([single], () => setInfoPanel({ open: false, panelMode: InfoPanelMode.Update }))}
                    onItemUpdated={() => refreshItems([single])}
                    // Library parity: properties embeds the preview (honors the tenant's
                    // hideAutoPreview; unsupported types render properties-only). Same
                    // single-item 0-of-1 nav clamp as the standalone preview above.
                    filePreview={!hideAutoPreview
                        && isSinglePreviewSupported
                        && infoPanel.panelMode === InfoPanelMode.Update
                        && <CMFilePreviewPanel
                            selectedItem={single}
                            searchSource={SearchSource.Api}
                            isPropertiesPreview
                            selectedItemIndex={0}
                            totalItemCount={1}
                        />}
                />
            )}
            {isVersionHistoryOpen && single && (
                <CMVersionHistoryModal
                    selectedItem={single}
                    showDialog={isVersionHistoryOpen}
                    isInOneDrive={false}
                    closeDialog={() => { setIsVersionHistoryOpen(false); setVersionHistoryContext(null); refreshItems([single]); }}
                    libraryContext={versionHistoryContext ?? singleContext?.panelContext}
                />
            )}
            {isRenameOpen && single && (
                <CMRenameModal
                    selectedItem={single}
                    showDialog={isRenameOpen}
                    closeDialog={() => setIsRenameOpen(false)}
                    closeOnSuccess={() => closeAndRefresh([single], () => setIsRenameOpen(false))}
                    isAdvancedSearch={true}
                    selectedDocumentLibraries={singleContext?.docUrlDropdown ? [singleContext.docUrlDropdown] : []}
                />
            )}
            {sharePanel.open && dialogItems.length > 0 && (
                <CMFileSharePanel
                    panelIsOpen={sharePanel.open}
                    closePanel={() => setSharePanel({ open: false, panelMode: SharePanelMode.Share })}
                    items={dialogItems}
                    mode={sharePanel.panelMode}
                    userAccount={userAccount}
                />
            )}
            {checkinDialog.open && dialogItems.length > 0 && (
                <CMCheckinModal
                    selectedItems={dialogItems}
                    showDialog={checkinDialog.open}
                    isInOneDrive={false}
                    isPublish={checkinDialog.isPublish}
                    closeDialog={() => setCheckinDialog({ open: false, isPublish: false, context: null })}
                    closeOnSuccess={() => closeAndRefresh(dialogItems, () => setCheckinDialog({ open: false, isPublish: false, context: null }))}
                    libraryContext={checkinDialog.context}
                />
            )}
            {isAppRejOpen && dialogItems.length > 0 && (
                <CMVersionAppRejModal
                    selectedItems={dialogItems}
                    showDialog={isAppRejOpen}
                    isInOneDrive={false}
                    closeDialog={() => setIsAppRejOpen(false)}
                    closeOnSuccess={() => closeAndRefresh(dialogItems, () => setIsAppRejOpen(false))}
                    libraryContext={appRejContext}
                />
            )}
            {isAutomationOpen && dialogItems.length > 0 && (
                <WebPanel isOpen={isAutomationOpen} closePanel={() => setIsAutomationOpen(false)} headerText={languageStrings.AutomationFlowRequest} styles={{ main: { overflow: "unset" } }}>
                    <AutomationPanel
                        files={dialogItems}
                        tenantGuid={tenantId}
                        requestor={userAccount?.username}
                        isContentManager={false}
                        closeClicked={() => setIsAutomationOpen(false)}
                        // Derived from the CAPTURED items (not the live selection) so a
                        // selection change while the panel is open can't retarget it.
                        listName={getLibraryLocation(dialogItems[0]?.[AGGREGATE_SOURCE_LIST_ID])?.displayName}
                        hookValidationLocation={pseudoDirectoryFor(dialogItems[0]?.[AGGREGATE_SOURCE_LIST_ID])}
                    />
                </WebPanel>
            )}
            {isSigningOpen && dialogItems.length > 0 && (
                <WebPanel isOpen={isSigningOpen} closePanel={() => setIsSigningOpen(false)} headerText={languageStrings.SigningRequest} styles={{ main: { overflow: "unset" } }}>
                    <SigningPanel
                        files={dialogItems}
                        tenantGuid={tenantId}
                        requestor={userAccount?.username}
                        isContentManager={false}
                        closeClicked={() => setIsSigningOpen(false)}
                        listName={getLibraryLocation(dialogItems[0]?.[AGGREGATE_SOURCE_LIST_ID])?.displayName}
                        hookValidationLocation={pseudoDirectoryFor(dialogItems[0]?.[AGGREGATE_SOURCE_LIST_ID])}
                    />
                </WebPanel>
            )}
            {isHixOpen && dialogItems.length > 0 && (
                <HixUploadPanel
                    isOpen={isHixOpen}
                    onDismiss={() => setIsHixOpen(false)}
                    // The client is preselected from the rows' own site: a client-group site's
                    // rows must not default to this site's client (none across several sites).
                    siteDisplayName={resolveHixSiteTitle(dialogItems.filter(isCmUploadableFile).map(row => row[AGGREGATE_SOURCE_SITE_NAME]), siteDisplayName)}
                    files={dialogItems.filter(isCmUploadableFile).map(row => ({ name: row.FileLeafRef, fileType: row[".fileType"]?.replace(".", ""), isFolder: false }))}
                    // Library parity: resolveCmHixUploadItems, except the drive context is
                    // per ROW (each aggregate row carries its own driveId/itemId) instead
                    // of the ambient current directory.
                    getUploadItems={() => Promise.all(dialogItems.filter(isCmUploadableFile).map(async row => ({
                        driveId: row.driveId,
                        itemId: await resolveRowItemId(row),
                        name: row.FileLeafRef,
                    } as IHixUploadItem)))}
                    getAccessToken={() => authModule.getGraphAccessToken()}
                />
            )}
            {pendingCopyMove && (
                // Mirrors the library view's first-stage dialog (ContentBodyFilesFolders,
                // rendered on `!moveToEnabled`) so both entry points acknowledge the
                // operation the same way before anything is armed. Cancelling here arms
                // nothing and leaves the selection intact.
                <CMConfirmModal
                    title={pendingCopyMove.mode === ContentManagerCopyMoveModeEnum.Move
                        ? languageStrings.MoveItems
                        : languageStrings.CopyItems}
                    // Same single instruction the library dialog uses. The count is
                    // deliberately not composed into it: the "N items to move" strings
                    // are chip labels that every caller lowercases, which mis-cases the
                    // German nouns mid-sentence — and the tray states the count in full
                    // the moment this is confirmed.
                    message={languageStrings.SelectDestinationDirectory}
                    showDialog={Boolean(pendingCopyMove)}
                    closeDialog={() => setPendingCopyMove(null)}
                    closeText={languageStrings.Cancel}
                    confirmText={languageStrings.Ok}
                    onConfirm={confirmMoveOrCopy}
                />
            )}
            {commandError && (
                <CMErrorModal
                    message={commandError}
                    showDialog={Boolean(commandError)}
                    closeDialog={() => setCommandError("")}
                />
            )}
        </>
    );

    return { slots, panels, openPropertiesPanel };
};
